import React, { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { placeOrderByCart } from "../../../node_modules/shops-query/src/modules/orderbycart/index.js";
import ConfirmationDialog from "../confirmationModal";

const PaymentComponent = ({
  cartAmount = 0,
  orderAddress,
  shopId,
  userId,
  shopPhone,
  cartItems = [],
  onOrderSuccess,
  onOrderFailure,
  onClose,
  siteConfig,
}) => {
  const [processing, setProcessing] = useState(""); // "paynow" or "cod" or ""
  const [confirmation, setConfirmation] = useState({
    isOpen: false,
    method: null,
    details: null
  });

  const isOnlinePaymentAvailable =
    siteConfig?.razorpayKey || siteConfig?.phonePeKey;

  // Helper to load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiateOrder = (method, details = null) => {
    setConfirmation({
      isOpen: true,
      method,
      details
    });
  };

  const executePlaceOrder = async () => {
    const { method, details } = confirmation;
    setConfirmation({ isOpen: false, method: null, details: null });
    
    try {
      setProcessing(method === "Cash On Delivery" ? "cod" : "paynow");
 
      if (!orderAddress) throw new Error("Please select a delivery address");
      if (!userId) throw new Error("User ID is required");
 
      const numericUserId = Number(userId);
      const numericShopId = Number(shopId);
 
      const variables = {
        userId: numericUserId,
        shopId: numericShopId,
        voucherNo: "1",
        orderType: method,
        customerId: numericUserId,
        customerName: orderAddress.name || "",
        customerMobile: orderAddress.phone || "",
        customerAddress: `${orderAddress.street || ""} ${orderAddress.city || ""},${orderAddress.state || ""},${orderAddress.country || ""}-${orderAddress.pincode || ""}`,
        pickuptime: "",
        feedback: "",
        rating: 1,
        shopPhone: shopPhone || "9688002474",
        billingAddress: Number(orderAddress.id),
        shippingAddress: Number(orderAddress.id),
        ...details // Spread online payment details if any
      };
 
      const result = await placeOrderByCart(variables);
 
      if (result?.[0]?.id) {
        onOrderSuccess(
          `Order Placed Successfully. Order Id: ${result[0].id}`
        );
      } else {
        throw new Error("Order Failed");
      }
    } catch (error) {
      console.error("Order Processing Error:", error);
      onOrderFailure(error.message || "Failed to place order");
    } finally {
      setProcessing("");
    }
  };

  const handleRazorpayPayment = async () => {
    try {
      if (!siteConfig?.razorpayKey) {
        throw new Error("No Payment Gateway Integrated");
      }

      setProcessing("paynow");
      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        throw new Error(
          "Razorpay SDK failed to load. Please check your internet connection."
        );
      }

      const options = {
        key: siteConfig.razorpayKey,
        amount: Math.ceil(cartAmount * 100),
        currency: "INR",
        name: siteConfig.name || "Boo Originals",
        description: `${siteConfig.name || "Boo"} Gateway`,
        image: "https://example.com/your_logo.png",
        handler: function (response) {
          initiateOrder("Online Payment", {
            id: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: response.razorpay_signature,
          });
        },
        prefill: {
          name: orderAddress?.name || "",
          email: "customer@example.com",
          contact: orderAddress?.phone || shopPhone || "6300000000",
        },
        theme: {
          color: "#F74F25", // Website brand color
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      onOrderFailure(err.message);
    } finally {
      setProcessing("");
    }
  };

  const formattedAmount =
    typeof cartAmount === "number" ? cartAmount.toFixed(2) : "0.00";

  return (
    <div className="flex flex-col gap-4 font-poppins">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-[18px] text-neutral-black">
          Complete Your Purchase
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <IoMdClose size={24} />
        </button>
      </div>

      <div className="bg-coupon-section rounded-lg p-5 border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-1">
          <p className="text-sm text-neutral-grey">Order Total</p>
          <p className="text-2xl font-bold text-primary-default">
            ₹{formattedAmount}
          </p>
        </div>
        <p className="text-xs text-neutral-grey">
          Delivery to: {orderAddress?.name}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mt-2">
        {isOnlinePaymentAvailable && (
          <button
            disabled={processing === "paynow" || !orderAddress || cartAmount <= 0}
            onClick={handleRazorpayPayment}
            className="flex-1 bg-primary-default hover:bg-primary-darker disabled:opacity-50 text-white font-semibold py-4 rounded-md uppercase text-[15px] transition-all duration-200 shadow-md active:scale-95"
          >
            {processing === "paynow" ? "Processing..." : "Pay Now"}
          </button>
        )}

        <button
          disabled={processing === "cod" || !orderAddress || cartAmount <= 0}
          onClick={() => initiateOrder("Cash On Delivery")}
          className="flex-1 bg-primary-default hover:bg-primary-darker disabled:opacity-50 text-white font-semibold py-4 rounded-md uppercase text-[15px] transition-all duration-200 shadow-md active:scale-95"
        >
          {processing === "cod" ? "Processing..." : "Cash On Delivery"}
        </button>
      </div>

      <p className="text-[11px] text-center text-neutral-grey mt-2">
        By clicking above, you agree to our Terms & Conditions.
      </p>

      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title="Confirm Order"
        message={`Are you sure you want to place this order via ${confirmation.method}?`}
        confirmLabel="YES, PLACE ORDER"
        cancelLabel="CANCEL"
        onConfirm={executePlaceOrder}
        onCancel={() => setConfirmation({ isOpen: false, method: null, details: null })}
      />
    </div>
  );
};

export default PaymentComponent;
